import { TGetOffersResponse } from 'lendingApi/lapiOverrides.types';

export const SIGNUP_ERROR_CODES = {
  CREDIT_CHECK: ['134', '183', '187'],
  CONFIRM_APPLICANT_DETAILS: ['186', '138'],
  DUPLICANT_GUARANTOR: ['136'],
};

export const SELF_SERVE_DATA_REQUIREMENT_ERROR_CODES = [
  ...SIGNUP_ERROR_CODES.CREDIT_CHECK,
  ...SIGNUP_ERROR_CODES.CONFIRM_APPLICANT_DETAILS,
];

export const getSuggestedOffer = (
  offers?: TGetOffersResponse['data']['offers'],
) => offers?.find((offer) => offer.is_suggested);

export const hasRequiredDocuments = (
  offer?: TGetOffersResponse['data']['offers'][number],
) =>
  offer?.data_requirements
    ?.find((requirement) => requirement.category === 'documents')
    ?.actions?.some(
      (action) => action.status === 'required' && action.is_optional === false,
    ) ?? false;

type TRequirementError = { code: string; type: string; detail: string };

const getDataRequirementActions = (
  offers: TGetOffersResponse['data']['offers'],
) => {
  if (!offers?.length) return;

  return offers.flatMap((offer) =>
    (offer.data_requirements ?? [])
      .filter((requirement) => requirement.category !== 'documents')
      .flatMap((requirement) => requirement.actions ?? []),
  );
};

const getActionResolutionMethods = (
  actions?: NonNullable<
    TGetOffersResponse['data']['offers'][number]['data_requirements']
  >[number]['actions'],
) => {
  if (!actions) return;

  return actions
    .filter((action) => action.status === 'error')
    .flatMap((action) => action.resolution_methods ?? []);
};

export const getDataRequirementErrors = (
  offers?: TGetOffersResponse['data']['offers'],
): TRequirementError[] | undefined => {
  if (!offers?.length) return;

  const errors: TRequirementError[] = [];
  const resolutionMethods = getActionResolutionMethods(
    getDataRequirementActions(offers),
  );

  for (const method of resolutionMethods ?? []) {
    if (method.status !== 'error') continue;
    for (const err of method.errors ?? []) {
      errors.push({
        code: err.code,
        type: method.type || '',
        detail: err.detail,
      });
    }
  }
  return errors;
};

export const getAutoBankStatements = (
  offers?: TGetOffersResponse['data']['offers'],
) => {
  if (!offers?.length) return;

  const autoBankStatementFlags = new Set([
    'auto_bs_in_decline',
    'has_invalid_cato_data',
    'lower_score_eligible',
  ]);

  return Boolean(
    offers?.some((offer) =>
      offer.data_requirements?.some((requirement) =>
        requirement.actions?.some(
          (action) =>
            action.reason && autoBankStatementFlags.has(action.reason),
        ),
      ),
    ),
  );
};
